import React, { useCallback, useMemo, useState } from "react";
import { Box, List, ListItem, ListItemText, Typography, TextField, Tooltip, ButtonGroup, Button } from "@mui/material";
import { useCollections } from "./hooks";
import { Small } from "components/typography";

const Filter = {
  All: "all",
  Primary: "primary",
  Secondary: "secondary"
};

const FormattedListItem = ({ collection, selectedId, handleSelect, filter }) => {
  return (
    <ListItem
      key={collection.id}
      onClick={() => handleSelect(collection)}
      sx={{
        flexDirection: "column",
        backgroundColor: selectedId && parseInt(selectedId) === collection.id ? "rgba(0, 0, 0, 0.04)" : "transparent",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.08)"
        }
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "row", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
        <ListItemText primary={collection.displayName} />
        <Typography variant="body2" color="text.secondary">{collection.status}</Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "row", width: "100%", justifyContent: "space-between" }}>
        <Small color="text.secondary">id: {collection.id}</Small>
        {filter === Filter.Primary && <Small color="text.secondary">order: {collection.order}</Small>}
      </Box>
      <Small color="text.secondary">{collection.internalNotes}</Small>
    </ListItem>
  );
};

const GenieCollectionsList = React.memo(({ onSelect, selectedId }) => {
  const { data: collections, isLoading, error } = useCollections();
  const [searchText, setSearchText] = useState("");
  const [filter, setFilter] = useState(Filter.All);

  const handleSelect = useCallback((collection) => {
    onSelect(collection);
  }, [onSelect]);

  const handleSearchTextChange = (event) => {
    setSearchText(event.target.value);
  };

  const { filteredAllCollections, childCollectionIds } = useMemo(() => {
    const ids = [];
    const filtered = collections?.filter((collection) => {
      collection.ParentGenieCollectionMeta?.forEach((parent) => {
        if (parent.childGenieCollectionId) {
          ids.push(parent.childGenieCollectionId);
        }
      });

      return collection.displayName.toLowerCase().includes(searchText.toLowerCase());
    }) ?? [];

    return { filteredAllCollections: filtered, childCollectionIds: ids };
  }, [collections, searchText]);

  const primaryCollections = useMemo(() => collections?.filter(collection => collection.isPrimary) ?? [], [collections]);
  const filteredPrimaryCollections = useMemo(() => {
    return primaryCollections?.filter(collection =>
      collection.displayName.toLowerCase().includes(searchText.toLowerCase())
    ) ?? [];
  }, [primaryCollections, searchText]);

  const secondaryCollections = useMemo(() => collections?.filter(collection => !collection.isPrimary) ?? [], [collections]);
  const collectionsWithChildren = useMemo(() => secondaryCollections?.filter(collection => collection.ParentGenieCollectionMeta.length > 0) ?? [], [secondaryCollections]);
  const childCollections = useMemo(() => secondaryCollections?.filter(collection => childCollectionIds.includes(collection.id)) ?? [], [secondaryCollections, childCollectionIds]);
  const standaloneCollections = useMemo(() => secondaryCollections?.filter(collection => collection.ParentGenieCollectionMeta.length === 0 && !childCollectionIds.includes(collection.id)) ?? [], [secondaryCollections, childCollectionIds]);

  const filteredCollectionsWithChildren = useMemo(() => collectionsWithChildren.filter(collection =>
    collection.displayName.toLowerCase().includes(searchText.toLowerCase())
  ), [collectionsWithChildren, searchText]);

  const filteredChildCollections = useMemo(() => childCollections.filter(collection =>
    collection.displayName.toLowerCase().includes(searchText.toLowerCase())
  ), [childCollections, searchText]);

  const filteredStandaloneCollections = useMemo(() => standaloneCollections.filter(collection =>
    collection.displayName.toLowerCase().includes(searchText.toLowerCase())
  ), [standaloneCollections, searchText]);

  if (isLoading) { return <Typography>Loading collections...</Typography>; }
  if (error) { return <Typography>Error loading collections: {error.message}</Typography>; }

  return (
    <Box
      sx={{
        width: "30%",
        maxHeight: "80vh",
        overflowY: "auto",
        borderRight: "1px solid #ddd",
        p: 2
      }}
    >
      <TextField
        fullWidth
        size="small"
        placeholder="Search by displayName"
        variant="outlined"
        value={searchText}
        onChange={handleSearchTextChange}
        sx={{ mb: 2 }}
      />
      <Box flexDirection="row" display="flex" alignItems="center">
        <ButtonGroup
          size="small"
          sx={{
            display: "flex",
            flex: 1,
            button: { flexGrow: 1 }
          }}
        >
          <Button
            onClick={() => setFilter(Filter.All)}
            variant={filter === Filter.All ? "contained" : "outlined"}
            flex={1}
          >
            All
          </Button>
          <Tooltip
            arrow
            title="These collections appear in the navbar on web and the top cutouts on the brands tab in app.
            The order is based on status: new, live, coming_soon. hidden is not displayed."
            placement="bottom-end"
            enterDelay={300}
          >
            <Button
              onClick={() => {
                setFilter(Filter.Primary);
              }}
              variant={filter === Filter.Primary ? "contained" : "outlined"}
            >
              Primary
            </Button>
          </Tooltip>
          <Button
            onClick={() => {
              setFilter(Filter.Secondary);
            }}
            variant={filter === Filter.Secondary ? "contained" : "outlined"}
          >
            Secondary
          </Button>
        </ButtonGroup>
      </Box>
      <List sx={{ display: filter === Filter.All ? "block" : "none" }}>
        {filteredAllCollections.map((collection) => (
          <FormattedListItem
            key={collection.id}
            collection={collection}
            selectedId={selectedId}
            handleSelect={handleSelect}
            filter={filter}
          />
        ))}
      </List>
      <List sx={{ display: filter === Filter.Primary ? "block" : "none" }}>
        {filteredPrimaryCollections.map((collection) => (
          <FormattedListItem
            key={collection.id}
            collection={collection}
            selectedId={selectedId}
            handleSelect={handleSelect}
            filter={filter}
          />
        ))}
      </List>

      <List sx={{ display: filter === Filter.Secondary ? "block" : "none" }}>
        <Typography variant="h6" gutterBottom>
          Collections with children
        </Typography>
        {filteredCollectionsWithChildren.map((collection) => (
          <FormattedListItem
            key={collection.id}
            collection={collection}
            selectedId={selectedId}
            handleSelect={handleSelect}
            filter={filter}
          />
        ))}
      </List>

      <List sx={{ display: filter === Filter.Secondary ? "block" : "none" }}>
        <Tooltip
          arrow
          title="These collections neither have children nor are children of other collections"
          placement="bottom-end"
          enterDelay={300}
        >
          <Typography variant="h6" gutterBottom>
            Standalone Collections
          </Typography>
        </Tooltip>
        {filteredStandaloneCollections.map((collection) => (
          <FormattedListItem
            key={collection.id}
            collection={collection}
            selectedId={selectedId}
            handleSelect={handleSelect}
            filter={filter}
          />

        ))}
      </List>

      <List sx={{ display: filter === Filter.Secondary ? "block" : "none" }}>
        <Tooltip
          arrow
          title="These collections display as children on other collection pages"
          placement="bottom-end"
          enterDelay={300}
        >
          <Typography variant="h6" gutterBottom>
            Child collections
          </Typography>
        </Tooltip>
        {filteredChildCollections.map((collection) => (
          <FormattedListItem
            key={collection.id}
            collection={collection}
            selectedId={selectedId}
            handleSelect={handleSelect}
            filter={filter}
          />
        ))}
      </List>
    </Box>
  );
});

GenieCollectionsList.displayName = "GenieCollectionsList";

export default GenieCollectionsList;
